import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '../../components/Button';
import { Checkbox, Grid, Icon, Input, Message, Modal, Table } from 'semantic-ui-react';
import { formatAmount, maskCnpj } from '../../services/masks';
import { EstateData } from './components/EstateData';
import { BankAccountData } from './components/BankAccountData';
import { EstateFormProps } from './UpdateEstateForm';
import { BondInsuranceData } from './components/BondInsuranceData';
import { FireInsuranceData } from './components/FireInsuranceData';
import { BrokerForUpdateEstateForm, TypeOfFeesType } from './types';
import { CapitalizationInsuranceData } from './components/CapitalizationInsuranceData';
import { LenderInsuranceConfig } from './components/LenderInsuranceConfig';
import { PromotionalSectionForUpdate } from './components/PromotionalSectionForUpdate';
import { specialCharRemove } from '../../utils/string.util';
import SectionLabel from '../../components/SectionLabel';
import { StepCard } from './styles';
import { get, put } from '../../services/request';
import { successNotification, warningNotification } from '../../services/notification';
import { AllowEstateUserPlEditToggle } from '../estates-form/components/AllowEstateUserPlEditToggle';
import { isBackofficeTestUser } from '../../util';

const EstateContainer = styled.div`
  height: 100%;
  padding-right: 5em;
`;

const handleFormInputChange = (data: any, formProps: any) => {
  const { setFieldValue } = formProps;
  const { field } = data;
  let { value } = data;

  if (field.name === 'estate[cnpj]') {
    value = maskCnpj(value);
  }

  if (
    ['name', 'company_name', 'estateDistrict', 'estateCity', 'estateComplement'].includes(
      field.name
    )
  ) {
    value = specialCharRemove(value);
  }

  if (field.name === 'estateNumber') {
    value = value.replace(/\D/g, '');
  }

  setFieldValue(field.name, value);
};

const ServiceSelector: React.FC<{ step: number; setStep: (s: number) => void }> = ({
  step,
  setStep
}) => {
  return (
    <Grid.Row>
      <Grid.Column width={4}>
        <StepCard
          active={step === 1}
          onClick={() => {
            setStep(1);
          }}
        >
          <span>
            <Icon name="shield" color={step === 1 ? undefined : 'grey'} /> Seguro Fiança
          </span>
        </StepCard>
      </Grid.Column>
      <Grid.Column width={5}>
        <StepCard
          active={step === 2}
          onClick={() => {
            setStep(2);
          }}
        >
          <span>
            <Icon name="fire" color={step === 2 ? undefined : 'grey'} /> Seguro Incêndio
          </span>
        </StepCard>
      </Grid.Column>
      <Grid.Column width={4}>
        <StepCard
          active={step === 3}
          onClick={() => {
            setStep(3);
          }}
        >
          <span>
            <Icon name="money bill alternate" color={step === 3 ? undefined : 'grey'} />{' '}
            Capitalização
          </span>
        </StepCard>
      </Grid.Column>
    </Grid.Row>
  );
};

interface CommissionData {
  brokerRepass?: boolean | undefined;
  id: string;
  type: string;
  insurerName: string;
  brokerCommission: number;
  estateCommission: number;
  variableProlabore: number;
  product: string;
}

interface ModalEditCommissionProps {
  productName: string;
  data: CommissionData[];
  updateCommissions: (data: CommissionData[]) => void;
}

const ModalEditCommission: React.FC<ModalEditCommissionProps> = ({
  productName,
  data,
  updateCommissions
}) => {
  const [commissions, setCommissions] = useState<CommissionData[]>(data);
  const [originalData, setOriginalData] = useState<CommissionData[]>(data);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setCommissions(data);
    setOriginalData(data);
  }, [data]);

  function handleChange(index: number, key: keyof CommissionData, value: number) {
    setCommissions(prev =>
      prev.map((item, i) => (i === index ? { ...item, [key]: value } : item))
    );
  }

  async function saveCommissions() {
    const changedItems = commissions.filter((current, idx) => {
      const original = originalData[idx];
      return (
        current.brokerCommission !== original.brokerCommission ||
        current.estateCommission !== original.estateCommission ||
        current.variableProlabore !== original.variableProlabore ||
        current.brokerRepass !== original.brokerRepass
      );
    });

    if (changedItems.length === 0) {
      warningNotification('Sem alterações', 'Nenhuma comissão foi alterada.');
      return;
    }

    // se tiver insurerName como Alfa ou Tokio, vamos validar se comissão da corretora + correção da imobiliária é igual a 70%
    // if (productName === 'Seguro Incêndio') {
    //   const invalidItems = changedItems.filter(
    //     item => item.brokerCommission + item.estateCommission !== 70
    //   );
    //   if (invalidItems.length > 0) {
    //     warningNotification(
    //       'Comissões inválidas',
    //       'A soma da comissão da corretora com a comissão da imobiliária deve ser igual a 70% para as seguradoras Alfa e Tokio.'
    //     );
    //     return;
    //   }
    // }

    try {
      const responses = await Promise.all(
        changedItems.map(item =>
          put('/estate-fee', {
            id: item.id,
            brokerFee: Number(item.brokerCommission),
            estateFee: Number(item.estateCommission),
            variableProlabore: Number(item.variableProlabore),
            brokerRepass: Boolean(item.brokerRepass)
          })
        )
      );
      console.log(responses);
      successNotification('Sucesso!', 'Comissões atualizadas com sucesso!');
      updateCommissions(commissions);
      setOpen(false);
    } catch (error) {
      console.error('Ocorreu um erro ao salvar:', error);
    }
  }

  return (
    <Modal
      closeIcon
      size="large"
      open={open}
      onClose={() => setOpen(false)}
      trigger={
        <Button
          text="Editar Comissões"
          size="medium"
          basic
          onClick={() => setOpen(true)}
          style={{ float: 'right', marginTop: '-4em', marginBottom: '1em' }}
        />
      }
    >
      <Modal.Header>Editar Comissões - {productName}</Modal.Header>
      <Modal.Content scrolling>
        <Table celled>
          <Table.Header fullWidth>
            <Table.Row>
              <Table.HeaderCell>SEGURADORA</Table.HeaderCell>
              <Table.HeaderCell>PRODUTO</Table.HeaderCell>
              <Table.HeaderCell>COMISSÃO CORRETORA</Table.HeaderCell>
              <Table.HeaderCell>COMISSÃO IMOBILIÁRIA</Table.HeaderCell>
              <Table.HeaderCell>COMISSÃO FRANQUEADO</Table.HeaderCell>
              <Table.HeaderCell>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  REPASSE VIA CORRETORA
                  {productName !== 'Capitalização' && (
                    <Checkbox
                      toggle
                      onChange={(_, { checked }) => {
                        setCommissions(prev =>
                          prev.map(comm => ({
                            ...comm,
                            brokerRepass: checked
                          }))
                        );
                      }}
                    />
                  )}
                </div>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {commissions.map((item, index) => (
              <Table.Row key={index}>
                <Table.Cell>{item.insurerName}</Table.Cell>
                <Table.Cell>
                  {TypeOfFeesType[item?.type as keyof typeof TypeOfFeesType]}
                </Table.Cell>
                <Table.Cell>
                  <Input
                    value={item.brokerCommission}
                    label="%"
                    labelPosition="right"
                    type="number"
                    disabled={
                      productName !== 'Seguro Fiança' && productName !== 'Seguro Incêndio'
                    }
                    onChange={(_, { value }) =>
                      handleChange(index, 'brokerCommission', Number(value))
                    }
                    style={{ maxWidth: 100 }}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Input
                    label="%"
                    labelPosition="right"
                    type="number"
                    disabled={productName === 'Capitalização'}
                    value={item.estateCommission}
                    onChange={(_, { value }) => {
                      handleChange(index, 'estateCommission', Number(value));

                      // if (productName === 'Seguro Incêndio') {
                      //   const numValue = Number(value);
                      //   let prolabore = 20;

                      //   if (numValue > 50) prolabore = 2;
                      //   else if (numValue > 40) prolabore = 4;
                      //   else if (numValue > 30) prolabore = 7;
                      //   else if (numValue > 20) prolabore = 10;
                      //   else if (numValue > 10) prolabore = 15;

                      //   handleChange(index, 'variableProlabore', prolabore);

                      //   // ajustar o brokerCommission para que a soma entre brokerCommission e estateCommission seja 70
                      //   const brokerCommission = Number(item.brokerCommission);
                      //   const estateCommission = Number(value);
                      //   const total = brokerCommission + estateCommission;
                      //   if (total !== 70) {
                      //     const result = 70 - estateCommission;
                      //     if (result >= 0)
                      //       handleChange(
                      //         index,
                      //         'brokerCommission',
                      //         70 - estateCommission
                      //       );
                      //   }
                      // }
                    }}
                    style={{ maxWidth: 100 }}
                  />
                </Table.Cell>

                <Table.Cell>
                  <Input
                    disabled={productName !== 'Seguro Fiança'}
                    label="%"
                    labelPosition="right"
                    type="number"
                    value={item.variableProlabore}
                    onChange={(_, { value }) =>
                      handleChange(index, 'variableProlabore', Number(value))
                    }
                    style={{ maxWidth: 100 }}
                  />
                </Table.Cell>

                <Table.Cell>
                  <Checkbox
                    disabled={productName === 'Capitalização'}
                    checked={item.brokerRepass}
                    onChange={(_, { checked }) => {
                      handleChange(index, 'brokerRepass', checked ? 1 : 0);
                    }}
                    toggle
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Modal.Content>

      <Modal.Actions>
        <Button text="Salvar" color="green" size="medium" onClick={saveCommissions} />
      </Modal.Actions>
    </Modal>
  );
};

const UpdateEstateFormContent = (props: EstateFormProps) => {
  const [step, setStep] = useState(1);
  const [commissions, setCommissions] = useState<any[]>();
  const { availableBrokersAndEstates, setFieldValue } = props;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedBroker: BrokerForUpdateEstateForm =
    availableBrokersAndEstates.brokers.length === 1
      ? {
        id: availableBrokersAndEstates.brokers[0].value.id,
        name: availableBrokersAndEstates.brokers[0].value.name
      }
      : {
        id: '',
        name: ''
      };
  useEffect(() => {
    if (selectedBroker) {
      setFieldValue('broker', selectedBroker);
    }
    async function loadCommissions() {
      const response = await get(`/estate-fee?estateId=${props.values.id}`);

      if (response) {
        setCommissions(
          response.map(
            (item: {
              id: string;
              insurer: { name: string; id: string };
              brokerFee: number;
              brokerRepass: boolean;
              variableProlabore: number;
              estateFee: number;
              type?: string;
              product: string;
            }) => ({
              id: item.id,
              type: item.type,
              insurerName: item.insurer.name,
              brokerCommission: item.brokerFee,
              estateCommission: item.estateFee,
              brokerRepass: item.brokerRepass,
              variableProlabore: item.variableProlabore,
              product: item.product
            })
          )
        );
      }
    }
    props.values.id && loadCommissions();
  }, [props.values.id, setFieldValue]);

  return (
    <EstateContainer>
      {isBackofficeTestUser(props.user) && (
        <Message info>
          Perfil de testes: você pode navegar pela edição, mas não pode salvar alterações.
        </Message>
      )}
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <Grid>
              <EstateData {...props} handleInputChange={handleFormInputChange} />
              <BankAccountData {...props} handleInputChange={handleFormInputChange} />
              <PromotionalSectionForUpdate
                values={props.values}
                setFieldValue={props.setFieldValue}
              />
              <LenderInsuranceConfig {...props} />
              <SectionLabel
                text="CONFIGURAR SERVIÇOS"
                ReactItem={() => (
                  <Icon color="blue" name="exclamation circle" style={{ fontSize: 18 }} />
                )}
              />

              <ServiceSelector step={step} setStep={(s: number) => setStep(s)} />
              {step === 1 ? (
                <BondInsuranceData {...props} handleInputChange={handleFormInputChange} />
              ) : step === 2 ? (
                <FireInsuranceData {...props} handleInputChange={handleFormInputChange} />
              ) : step === 3 ? (
                <CapitalizationInsuranceData
                  {...props}
                  handleInputChange={handleFormInputChange}
                />
              ) : (
                <></>
              )}
            </Grid>
          </Grid.Column>

          <Grid.Column width={16}>
            <SectionLabel
              text={`COMISSIONAMENTO ${step === 1
                ? 'SEGURO FIANÇA'
                : step === 2
                  ? 'SEGURO INCÊNDIO'
                  : 'CAPITALIZAÇÃO'
                }`}
            />
            {step === 2 && (
              <AllowEstateUserPlEditToggle
                checked={props.values.allowEstateUserPlEdit}
                onChange={checked =>
                  props.setFieldValue('allowEstateUserPlEdit', checked)
                }
              />
            )}
            <ModalEditCommission
              productName={
                step === 1
                  ? 'Seguro Fiança'
                  : step === 2
                    ? 'Seguro Incêndio'
                    : 'Capitalização'
              }
              data={
                commissions?.filter(
                  item =>
                    item?.product ===
                    (step === 1
                      ? 'BOND_INSURANCE'
                      : step === 2
                        ? 'FIRE_INSURANCE'
                        : 'CAPITALIZATION')
                ) || []
              }
              updateCommissions={c => {
                const updatedCommissions = c.filter(
                  item =>
                    item?.product ===
                    (step === 1
                      ? 'BOND_INSURANCE'
                      : step === 2
                        ? 'FIRE_INSURANCE'
                        : 'CAPITALIZATION')
                );
                const others = commissions?.filter(
                  item =>
                    item?.product !==
                    (step === 1
                      ? 'BOND_INSURANCE'
                      : step === 2
                        ? 'FIRE_INSURANCE'
                        : 'CAPITALIZATION')
                );
                if (others) setCommissions([...others, ...updatedCommissions]);
              }}
            />
            <Table celled>
              <Table.Header fullWidth>
                <Table.Row>
                  <Table.HeaderCell>SEGURADORA</Table.HeaderCell>
                  <Table.HeaderCell>PRODUTO</Table.HeaderCell>
                  <Table.HeaderCell>COMISSÃO CORRETORA</Table.HeaderCell>
                  <Table.HeaderCell>COMISSÃO IMOBILIÁRIA</Table.HeaderCell>
                  <Table.HeaderCell>COMISSÃO FRANQUEADO</Table.HeaderCell>
                  <Table.HeaderCell>REPASSE VIA CORRETORA</Table.HeaderCell>
                </Table.Row>
                {commissions
                  ?.filter(
                    item =>
                      item.product ===
                      (step === 1
                        ? 'BOND_INSURANCE'
                        : step === 2
                          ? 'FIRE_INSURANCE'
                          : 'CAPITALIZATION')
                  )
                  ?.map((item, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>{item?.insurerName}</Table.Cell>
                      <Table.Cell>
                        {TypeOfFeesType[item?.type as keyof typeof TypeOfFeesType]}
                      </Table.Cell>
                      <Table.Cell>{Number(item?.brokerCommission)}%</Table.Cell>
                      <Table.Cell>{Number(item?.estateCommission)}%</Table.Cell>
                      <Table.Cell>
                        {`${step === 3 ? 'R$ ' : ''}${Number(item?.variableProlabore)}${step !== 3 ? '%' : ''
                          }`}
                      </Table.Cell>
                      <Table.Cell>
                        <Checkbox checked={item.brokerRepass} toggle disabled />
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Header>
            </Table>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column textAlign="left" width={16}>
            <Button
              text="Atualizar"
              color="green"
              size="large"
              type="submit"
              onClick={() => props.onSubmit(props.values)}
              disabled={isBackofficeTestUser(props.user)}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </EstateContainer>
  );
};

export default UpdateEstateFormContent;
